import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridDetailRowProps, GridItemChangeEvent } from '@progress/kendo-react-grid';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GridWrapper from 'src/components/GridWrapper';
import useScrollDown from 'src/hooks/useScrollDown';
import useWindowSize from 'src/hooks/useWindowSize';
import {
  AssignMultipleButtonPermissions,
  AssignSingleButtonPermissions,
  ShowNotePermissions,
} from 'src/sections/view_invoice_budget_period/index';
import { SetState } from 'src/types/generic';
import { hasPermission } from 'src/utils/JWT';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../invoice_details/InvoiceDetailsContext';
import InvoiceBudgetPeriodToolBar from './InvoiceBudgetPeriodToolBar';
import {
  useInvoiceBudgetPeriodColumn,
  useInvoiceBudgetPeriodSubColumn,
} from './useInvoiceBudgetPeriodColumn';
const DATA_ITEM_KEY: string = 'rowId';
const defaultProps = {
  DataItemKey: DATA_ITEM_KEY,
  resizable: true,
  showColumnMenu: false,
} as const;

interface InvoiceBudgetPeriodTableProps {
  isTableLoading: boolean;
  dataSource: any[];
  handleItemChange: (e: GridItemChangeEvent) => void;
  handleAddRow: VoidFunction;
  handleAutoDistributution: VoidFunction;
  distributionType: 'Percentage' | 'Amount';
  onDistributionChange: (e: DropDownListChangeEvent) => void;
  isClick: boolean;
  setIsClick: SetState<boolean>;
}

const InvoiceBudgetPeriodTable: FC<InvoiceBudgetPeriodTableProps> = ({
  isTableLoading,
  dataSource,
  handleItemChange,
  handleAddRow,
  handleAutoDistributution,
  distributionType,
  onDistributionChange,
  isClick,
  setIsClick,
}) => {
  const { t } = useTranslation();
  const mpaPermissionNumber = useContextSelector(
    InvoiceContext,
    (value) => value.mpaPermissionNumber
  );
  const hasDistributionPermission = useContextSelector(
    InvoiceContext,
    (value) => value.hasDistributionPermission
  );
  const isInvoiceMPAEditable = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceMPAEditable
  );
  const invoiceBudgetPeriodColumns = useInvoiceBudgetPeriodColumn();
  const invoiceBudgetPeriodSubColumns = useInvoiceBudgetPeriodSubColumn();
  const title = useContextSelector(InvoiceContext, (value) => value.title);

  const tableRef = useScrollDown(isClick, setIsClick, [dataSource]);
  const { height } = useWindowSize();
  const [hideScrollBar, setScrollBar] = useState(false);

  useEffect(() => {
    if (tableRef.current?.element) {
      const gridHeight = tableRef.current.element.clientHeight;
      if (invoiceBudgetPeriodColumns[5]?.width) {
        if (gridHeight > height - 220) {
          setScrollBar(true);
          invoiceBudgetPeriodColumns[5].width = 146 - 17;
        } else {
          setScrollBar(false);
          invoiceBudgetPeriodColumns[5].width = 146;
        }
      }
    }
  }, [height, invoiceBudgetPeriodColumns, tableRef]);

  const DetailComponent = useCallback(
    (props: GridDetailRowProps) => (
      <GridWrapper
        dataSource={props?.dataItem?.items}
        columns={invoiceBudgetPeriodSubColumns}
        DataItemKey="field"
        resizable={true}
        style={{ maxHeight: '120px' }}
        hideScrollBar={hideScrollBar}
      />
    ),
    [hideScrollBar, invoiceBudgetPeriodSubColumns]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <InvoiceBudgetPeriodToolBar
        handleAddRow={handleAddRow}
        handleAutoDistributution={handleAutoDistributution}
        distributionType={distributionType}
        onDistributionChange={onDistributionChange}
      />
    ),
    [distributionType, handleAddRow, handleAutoDistributution, onDistributionChange]
  );

  const isSinglePeriodAssign = hasPermission(AssignSingleButtonPermissions, mpaPermissionNumber);
  const isMultiplePeriodAssign = hasPermission(
    AssignMultipleButtonPermissions,
    mpaPermissionNumber
  );
  const isShowNote = hasPermission(ShowNotePermissions, mpaPermissionNumber);

  return (
    <section>
      <div className="my-4">
        {hasDistributionPermission && (
          <p className="text-sm font-medium">
            {t(
              `Choose accounting periods and corresponding distribution % or $ for total ${title} $ amount.`
            )}
          </p>
        )}
        {isSinglePeriodAssign && (
          <p className="text-sm">
            {t(
              `Click 'Convert to Single Period' button to remove multiple period allocations and assign ${title} total $ amount to the most recent period from the PO.`
            )}
          </p>
        )}
        {isMultiplePeriodAssign && (
          <p className="text-sm">
            {t(
              "Click 'Convert to Multi Period Allocations' button to inherit multiple period allocations from the PO."
            )}
          </p>
        )}

        {isShowNote && (
          <p className="text-sm mt-2">
            {t(
              'Note: You do not have required permission to add or edit multiple periods and allocations. Please contact your administrator for further support if needed.'
            )}
          </p>
        )}
      </div>
      <div className="h-full">
        <GridWrapper
          id="invoiceBudgetPeriodGrid"
          columns={invoiceBudgetPeriodColumns}
          dataSource={dataSource}
          detail={DetailComponent}
          onItemChange={handleItemChange}
          loading={isTableLoading}
          {...defaultProps}
          gridToolbarChildren={isInvoiceMPAEditable ? gridToolbarChildren : undefined}
          ref={tableRef}
          style={{
            maxHeight: height - 210,
          }}
          isCustomExpandableRow
          className={hideScrollBar ? '' : 'auto-scrollbar'}
          hideScrollBar={hideScrollBar}
        />
      </div>
    </section>
  );
};

export default InvoiceBudgetPeriodTable;
